import ActionMenu from "Components/Common/ActionMenu";
import FullScreenLoader from "Components/Common/FullScreenLoader";
import PageHeaderActions from "Components/Common/PageHeaderActions";
import { StatsCardProps } from "Components/Common/StatsCard";
import StatsGroup from "Components/Common/StatsGroup";
import TableContainer from "Components/Common/TableContainer";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { GlobalImage } from "config/image";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Container } from "reactstrap";
import {
  adminDeleteUser,
  adminHome,
  adminUsersList,
  getConfig,
  initiateImpersonate,
  manageDisableUser,
} from "slices/thunks";
import { formatLocalDate } from "utils/formatDates";
import { capitalizeText } from "utils/formatText";
import { ErrorHandler } from "utils/toast";

const AdminDashboard = () => {
  const dispatch = useAppDispatch();
  const { homeData, isLoading, usersList } = useAppSelector(
    (state) => state.admin,
  );
  const { user } = useAppSelector((state) => state.auth);
  const [searchText, setSearchText] = useState("");
  const navigate = useNavigate();
  const [isImpersonateLoading, setIsImpersonateLoading] =
    useState<boolean>(false);
  const [isActiveLoading, setIsActiveLoading] = useState<boolean>(false);

  const dashboardStatsData: StatsCardProps[] = useMemo(
    () => [
      {
        title: "TOTAL USERS",
        icon: "TotalUsers",
        value: homeData?.user_count || 0,
        suffix: "",
        prefix: "",
        separator: ",",
        decimal: 0,
        tooltipMessage: "Total number of users",
        isTooltip: true,
      },
      {
        title: "ACTIVE SUBSCRIPTIONS",
        icon: "SparkleRed",
        value: homeData?.subscription_count || 0,
        suffix: "",
        prefix: "",
        separator: ",",
        decimal: 0,
        tooltipMessage: "Total number of active subscriptions",
        isTooltip: true,
      },
      {
        title: "QUOTES & INVOICES CREATED",
        icon: "BigBlueReceipt",
        value: (homeData?.quote_count || 0) + (homeData?.invoice_count || 0),
        suffix: "",
        prefix: "",
        separator: ",",
        decimal: 0,
        tooltipMessage: "Total number of quotes and invoices created",
        isTooltip: true,
      },
    ],
    [homeData],
  );

  useEffect(() => {
    dispatch(getConfig());
    dispatch(adminHome({}));
    dispatch(adminUsersList({ search: searchText }));
  }, [dispatch, searchText]);

  const handleDateFilter = async (range: { start: Date; end: Date }) => {
    console.log("Selected date range:", range);

    const formattedRange = {
      start_date: formatLocalDate(range.start),
      end_date: formatLocalDate(range.end),
    };
    console.log("Formatted date range for API:", formattedRange);

    try {
      await dispatch(adminHome(formattedRange))?.unwrap();
      await dispatch(adminUsersList({}))?.unwrap();
    } catch (error) {
      console.error("Error fetching home data with date filter:", error);
      ErrorHandler(error);
    }
  };

  const handleSearchUser = (searchText: string) => {
    setSearchText(searchText);
  };

  const handleImpersonate = useCallback(
    async (userId: number) => {
      try {
        setIsImpersonateLoading(true);
        await dispatch(initiateImpersonate(userId))?.unwrap();
        navigate("/dashboard", { replace: true });
      } catch (error) {
        console.error("Error initiating impersonation:", error);
        ErrorHandler(error);
      } finally {
        setIsImpersonateLoading(false);
      }
    },
    [dispatch, navigate],
  );

  const handleToggle = useCallback(
    async (id: number) => {
      try {
        setIsActiveLoading(true);
        await dispatch(manageDisableUser(id));
      } catch (error) {
        console.log("Error toggling user active status:", error);
        ErrorHandler(error);
      } finally {
        setIsActiveLoading(false);
      }
    },
    [dispatch],
  );

  const handleRemoveUser = useCallback(
    async (userId: number) => {
      try {
        await dispatch(adminDeleteUser(userId))?.unwrap();
        await dispatch(adminHome({}))?.unwrap();
      } catch (error) {
        console.error("Error removing user:", error);
        ErrorHandler(error);
      }
    },
    [dispatch],
  );

  const columns = useMemo(
    () => [
      {
        header: "USER",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: any) => {
          const user = row.original;
          return (
            <div className="d-flex align-items-center gap-2">
              <img
                src={user.avatar || GlobalImage?.userPlaceHolder}
                alt={user.name}
                className="user-img rounded-circle"
                style={{ width: 36, height: 36 }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    GlobalImage?.userPlaceHolder || "";
                }}
              />
              <span className="fw-medium text-black2D fs-14">
                {user.name ?? "-"}
              </span>
            </div>
          );
        },
      },
      {
        header: "TRADE",
        accessorKey: "trade",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "COMPANY",
        accessorKey: "company_name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "TEAM",
        accessorKey: "team_count",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "QUOTES",
        accessorKey: "quotes_count",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "INVOICES",
        accessorKey: "invoices_count",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "PHONE",
        accessorKey: "phone",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "EMAIL",
        accessorKey: "email",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "SUBSCRIPTION",
        accessorKey: "subscription_status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {capitalizeText(cell.getValue())}
          </span>
        ),
      },
      {
        header: "IS ACTIVE",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const isActive = cell.getValue();
          const rowId = cell?.row?.original?.id;

          return (
            <span
              className={`toggle-switch ${isActive ? "on" : "off"}`}
              onClick={() => handleToggle(rowId)}
            >
              <span className={`toggle-label ${isActive ? "on" : "off"}`}>
                {isActive ? "Yes" : "No"}
              </span>
              <div className={`toggle-circle ${isActive ? "on" : "off"}`} />
            </span>
          );
        },
      },
      {
        header: "ACTION",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <ActionMenu
              isView
              isDelete
              isImpersonate
              onView={() =>
                navigate("/user-detail", {
                  state: { userDetail: row.original },
                })
              }
              onImpersonate={handleImpersonate.bind(null, row.original.id)}
              onDelete={handleRemoveUser.bind(null, row.original.id)}
            />
          );
        },
      },
    ],
    [handleImpersonate, handleRemoveUser, handleToggle, navigate],
  );

  document.title = "Admin Dashboard";
  return (
    <div className="page-content">
      <FullScreenLoader
        isLoading={isImpersonateLoading}
        message="Switching to user account..."
      />
      <Container fluid>
        <PageHeaderActions
          isDateRangeSelector
          title={moment().format("dddd, Do MMM")}
          subtitle={` Welcome back, ${user?.name}! 👋`}
          onDateSelect={handleDateFilter}
        />
        <StatsGroup items={dashboardStatsData} />
        <Card id="membersTable" className="br-10 bg-white mt-4">
          <CardBody className="pt-0">
            <TableContainer
              columns={columns}
              data={usersList?.slice(0, 5)}
              isGlobalFilter={true}
              divClass="table-responsive table-card mb-2 bg-white"
              tableClass="align-middle table-nowrap table-borderless"
              thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5"
              theadClass="table-light"
              SearchPlaceholder="Search User"
              searchAlign="right"
              showPagination={false}
              isInitialLoading={isLoading && !isActiveLoading}
              leftContent={
                <p className="m-0 fw-medium fs-16 font-black2D">
                  Recent Signups
                </p>
              }
              onSearch={handleSearchUser}
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default AdminDashboard;
